import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SxProps,
} from '@mui/material';
import { type RefCallback, useId } from 'react';

type Option = { label: string; value: string };

type LAISelectProps<Value = string | string[]> = {
  name: string;
  disabled?: boolean;
  options: Option[];
  sx?: SxProps;
  fullWidth?: boolean;
  label?: string;
  onBlur?: () => void;
  renderValue?: (value: Value) => string;
  readOnly?: boolean;
  hookFormRef: RefCallback<HTMLSelectElement>;
  helperText?: string;
  error?: boolean;
} & (
  | {
      multiple: true;
      value: Value;
      onChange: (value: Value) => void;
    }
  | {
      multiple?: false;
      value: Value;
      onChange: (value: Value) => void;
    }
);

/**
 * A controlled TextField that integrates react-hook-form with MUI
 */
export const LAISelect = <Value = unknown,>({
  name,
  disabled,
  options,
  label,
  sx,
  fullWidth,
  multiple,
  value,
  onChange,
  onBlur,
  renderValue,
  readOnly,
  hookFormRef: ref,
  helperText,
  error,
}: LAISelectProps<Value>) => {
  const labelId = `select-label-${useId()}`;
  const selectId = `select-input-${useId()}`;

  return (
    <FormControl sx={sx} fullWidth={fullWidth} size="small">
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        ref={ref}
        name={name}
        disabled={disabled}
        id={selectId}
        labelId={labelId}
        value={value}
        label={label}
        multiple={multiple}
        onBlur={onBlur}
        readOnly={readOnly}
        onChange={(e) => {
          onChange(e.target.value as Value);
        }}
        size="small"
        renderValue={renderValue}
      >
        {options.map((option) => {
          return (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          );
        })}
      </Select>
      {helperText ? <FormHelperText error={error}>{helperText}</FormHelperText> : null}
    </FormControl>
  );
};
